import { HostUIProvider } from "./apps/hostUI";
import { ProductTour } from "./tour/ProductTour";
import { PRODUCT_TOUR, requestProductTour } from "./tour/config";
import { useProductTourDismissed } from "./tour/useProductTourCompleted";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AppIcon } from "@apteva/ui-kit";
import { useProjects } from "../hooks/useProjects";
import { useAuth } from "../hooks/useAuth";
import { useAudience, type AudienceSection } from "../hooks/useAudience";
import { AccountMenu } from "./AccountMenu";
import { NotificationsTray } from "./NotificationsTray";
import { PlatformUpdateAction } from "./PlatformUpdateAction";
import {
  apps,
  instances,
  platform,
  platformHelper,
  type Agent,
  type PlatformStatus,
} from "../api";
import { NewAgentButton } from "./NewAgentButton";
import { AgentMark } from "./AgentMark";
import { RealtimeVoiceDock } from "../state/RealtimeVoiceContext";
import { ChatAssistantDock } from "./chat/ChatAssistantDock";
import {
  preferredSidebarAppNames,
  SidebarAppManager,
  useProjectUILayout,
  workspacePages,
} from "./apps/contributions";

// Sidebar APPS section visible-cap. Above this, the overflow row
// collapses the rest behind a "More apps (N)" toggle. Five is the
// "shows above the fold on a 720-tall screen plus the MANAGE
// section underneath" threshold — picked empirically.
const SIDEBAR_APPS_VISIBLE = 5;

type PlatformNavIcon = "dashboard" | "pages" | "agents" | "monitor" | "integrations" | "apps" | "skills" | "usage" | "settings";

const platformNavIcons: Record<string, PlatformNavIcon> = {
  "/": "dashboard",
  "/agents": "agents",
  "/monitor": "monitor",
  "/integrations": "integrations",
  "/apps": "apps",
  "/skills": "skills",
  "/analytics": "usage",
  "/settings": "settings",
};

export function Layout() {
  const { t } = useTranslation();
  const { audience, shows } = useAudience();
  const [version, setVersion] = useState("");
  const [versionTip, setVersionTip] = useState("");
  const [platformStatus, setPlatformStatus] = useState<PlatformStatus | null>(
    null,
  );
  const [helperActivated, setHelperActivated] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  // Sidebar APPS overflow toggle. Default collapsed so a long
  // install list (~22 apps in the boot we saw) doesn't push the
  // MANAGE section off-screen. Persisted to localStorage so the
  // expanded-state survives page reloads without a backend setting.
  const [showAllApps, setShowAllApps] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebar:showAllApps") === "1";
    } catch {
      return false;
    }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebar:collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem("sidebar:collapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);
  const toggleShowAllApps = useCallback(() => {
    setShowAllApps((v) => {
      const next = !v;
      try {
        localStorage.setItem("sidebar:showAllApps", next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);
  const [refreshing, setRefreshing] = useState(false);
  const { projects, currentProject, setCurrentProject } = useProjects();
  const { project: projectUILayout, document: pageLayout } = useProjectUILayout(currentProject?.id);
  const pageNav = workspacePages(pageLayout, currentProject?.id)
    .filter((page) => page.pinned !== false)
    .map((page) => ({ to: `/pages/${encodeURIComponent(page.id)}?${page.scope === "global" ? "scope=global" : `project=${encodeURIComponent(currentProject!.id)}`}`, label: page.title }));
  const [sidebarAppsOpen, setSidebarAppsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const tourDismissed = useProductTourDismissed();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const tourSidebar = useRef({ collapsed: false });
  const beginTour = useCallback(() => { tourSidebar.current = { collapsed: sidebarCollapsed }; }, [sidebarCollapsed]);
  const prepareTour = useCallback((navigation: boolean) => {
    setMobileNavOpen(navigation && window.innerWidth < 768);
    if (navigation) setSidebarCollapsed(false);
  }, []);
  const endTour = useCallback(() => {
    setSidebarCollapsed(tourSidebar.current.collapsed);
    setMobileNavOpen(false);
  }, []);
  const [sidebarAgents, setSidebarAgents] = useState<Agent[]>([]);

  const sidebarScope = useRef(currentProject?.id);
  sidebarScope.current = currentProject?.id;
  const sidebarPending = useRef(new Set<string>());
  const refreshSidebarAgents = useCallback(() => {
    if (audience !== "personal" || !currentProject?.id) {
      setSidebarAgents([]);
      return;
    }
    const id = currentProject.id;
    if (document.hidden || sidebarPending.current.has(id)) return;
    sidebarPending.current.add(id);
    instances.list(id)
      .then((rows) => { if (sidebarScope.current === id) setSidebarAgents(rows || []); })
      .catch(() => { if (sidebarScope.current === id) setSidebarAgents([]); })
      .finally(() => sidebarPending.current.delete(id));
  }, [audience, currentProject?.id]);

  useEffect(() => {
    refreshSidebarAgents();
    window.addEventListener("apteva:agents-changed", refreshSidebarAgents);
 document.addEventListener("visibilitychange", refreshSidebarAgents);
    const timer = window.setInterval(refreshSidebarAgents, 10_000);
    return () => {
      window.removeEventListener("apteva:agents-changed", refreshSidebarAgents);
 document.removeEventListener("visibilitychange", refreshSidebarAgents);
      window.clearInterval(timer);
    };
  }, [refreshSidebarAgents]);

  // The authenticated dashboard is a viewport shell: pages provide their own
  // scroll containers. Keep the browser document itself locked while Layout is
  // mounted. Without this, Chromium can propagate the overflow geometry of a
  // deeply nested runtime stream to <html> even though every visible ancestor
  // is height-constrained, which lets the operator scroll below the app into a
  // large blank area. Cleanup restores ordinary document scrolling for login
  // and onboarding routes.
  useEffect(() => {
    const roots = [document.documentElement, document.body];
    for (const root of roots) root.classList.add("apteva-shell-active");
    return () => {
      for (const root of roots) root.classList.remove("apteva-shell-active");
    };
  }, []);

  useEffect(() => {
    if (user === false) {
      // Tear down the telemetry SSE on logout. setProjectId(null) is the
      // documented disconnect path.
      if (typeof window !== "undefined") {
        window.__aptevaTelemetryBus?.setProjectId(null);
      }
    }
  }, [user]);

  // Bind the telemetry bus to the active project. The bus opens a
  // single /telemetry/stream?all=1&project_id=… SSE and multiplexes
  // events out to every component that calls
  // useTelemetryEvents() / window.__aptevaTelemetryBus.subscribe().
  // Per-page consumers (chat thinking strip, ActivityFeed, fleet
  // views) reuse this one stream instead of each opening their own,
  // which used to be the dominant contributor to the connection
  // budget on the dashboard.
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (location.pathname === "/monitor") return; // Monitor owns its fleet scope.
    const params = new URLSearchParams(location.search);
    const workspace = location.pathname === "/" || location.pathname.startsWith("/pages/") || (location.pathname === "/settings" && params.get("tab") === "pages");
    const requested = params.get("project");
    const selected = workspace && projects.some(project => project.id === requested) ? requested : currentProject?.id;
    const global = workspace && ["global", "all"].includes(params.get("scope") || "");
    window.__aptevaTelemetryBus?.setProjectId(global ? "*" : selected ?? null);
  }, [user, currentProject?.id, projects, location.pathname, location.search]);

  // Pull the platform-update status so the sidebar version footer can
  // show "update available" inline with the current version. The
  // server polls upstream every few hours; we re-read on dashboard
  // load so a refresh after an update lands shows the new state.
  useEffect(() => {
    if (!user) return;
    platform
      .status()
      .then(setPlatformStatus)
      .catch(() => {});
  }, [user]);

  const refreshHelperAvailability = useCallback(() => {
    if (!user) {
      setHelperActivated(false);
      return;
    }
    platformHelper
      .status()
      .then((status) => setHelperActivated(status.activated))
      .catch(() => setHelperActivated(false));
  }, [user]);

  useEffect(() => {
    refreshHelperAvailability();
    window.addEventListener("apteva:helper-changed", refreshHelperAvailability);
    return () => {
      window.removeEventListener("apteva:helper-changed", refreshHelperAvailability);
    };
  }, [refreshHelperAvailability]);

  const refreshPlatformStatus = useCallback(async () => {
    setRefreshing(true);
    try {
      const next = await platform.refresh();
      setPlatformStatus(next);
    } catch {
      /* keep last view; error surfaces server-side in `error` field */
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // /version now returns the full component breakdown — apteva (umbrella),
    // cli, dashboard, integrations, core, plus the build timestamp.
    // Display the umbrella in the sidebar footer; hover to see the full
    // breakdown via title. Fall back to the old `version` field for
    // older server binaries that haven't shipped the new handler yet.
    fetch("/version")
      .then((r) => r.json())
      .then((d) => {
        // Show the CLI version as the primary label — that's the one that
        // gets bumped per release (apteva/package.json). Fall back to the
        // umbrella/package version for older server binaries that predate
        // this change.
        const primary = d.cli || d.apteva || d.version || "";
        setVersion(primary);
        const parts: string[] = [];
        if (d.apteva) parts.push(`apteva ${d.apteva}`);
        if (d.cli) parts.push(`cli ${d.cli}`);
        if (d.dashboard) parts.push(`dashboard ${d.dashboard}`);
        if (d.integrations) parts.push(`integrations ${d.integrations}`);
        if (d.core) parts.push(`core ${d.core}`);
        if (d.build) parts.push(`build ${d.build}`);
        setVersionTip(parts.join("\n"));
      })
      .catch(() => {});
  }, []);

  // Sidebar is split into three semantic groups so the user can tell
  // platform-built surfaces from installed-app surfaces at a glance:
  //
  //   primaryNav — the dashboard's daily verbs (no section label;
  //                this group IS the platform's default home).
  //   appNav     — pages contributed by installed apps (header: APPS).
  //                Each entry carries the app's manifest icon so the
  //                visual cue matches the Apps page.
  //   manageNav  — platform-administration verbs (header: MANAGE).
  //                Things you do TO the platform, not WITH it.
  //
  // Entries carrying a `section` are gated by the audience axis (see
  // hooks/useAudience). Entries without one are visible to everyone.
  // Routes stay registered either way — hiding a sidebar link never
  // unmounts a route, so deep links and agent-driven navigation keep
  // working at every audience.
  const primaryNav = [
    { to: "/", label: t("nav.dashboard"), section: "nav.dashboard" },
    { to: "/agents", label: t("nav.agents"), section: "nav.agents" },
    { to: "/monitor", label: t("nav.monitor"), section: "nav.monitor" },
  ].filter((item) => !item.section || shows(item.section as AudienceSection));
  const manageNav = [
    { to: "/integrations", label: t("nav.integrations"), section: "nav.integrations" },
    { to: "/apps", label: t("nav.apps"), section: "nav.apps" },
    { to: "/skills", label: t("nav.skills"), section: "nav.skills" },
    { to: "/analytics", label: t("nav.usage"), section: "nav.usage" },
    { to: "/settings", label: t("nav.settings") },
  ].filter((item) => {
    if (item.section && !shows(item.section as AudienceSection)) return false;
    if (!user || user.role === "admin") return true;
    if (item.to === "/apps" && !user.capabilities.app_installation) return false;
    return true;
  });
  // App-contributed entries from any installed app declaring a
  // `provides.ui_panels` entry with slot=project.page. Each one
  // becomes a sidebar link to /apps/<name>/page rendered via
  // AppProjectPage. Fetched per project so installs scoped to one
  // project don't bleed into another's sidebar.
  //
  // Refresh policy: load on project switch, and again whenever the
  // window dispatches an "apteva:apps-changed" event. The Apps page
  // fires that event after install/uninstall + while polling pending
  // rows, so a freshly-installed app's sidebar entry appears the
  // instant the install flips to running. A 5s background poll is
  // there as a safety net for events we somehow miss.
  const [appNav, setAppNav] = useState<
    {
      name: string;
      to: string;
      label: string;
      icon?: string;
      iconStyle?: "image" | "monochrome";
      suggested?: boolean;
    }[]
  >([]);
  // Fetch epoch — every effect run bumps this and only the latest run
  // is allowed to write state. Without this, an in-flight unfiltered
  // fetch (currentProject not yet hydrated) can race with a later
  // project-filtered fetch and "win" by resolving second, leaving the
  // sidebar showing every project's panels duplicated. The duplicate-
  // Files-entry bug came from exactly this race.
  const fetchEpochRef = useRef(0);
  const refreshAppNav = useCallback(() => {
    // Skip the call when the project context isn't ready yet —
    // apps.list(undefined) hits /api/apps with no project_id filter,
    // which returns EVERY install (cross-project leak in the sidebar).
    if (!currentProject?.id) {
      setAppNav([]);
      return;
    }
    const epoch = ++fetchEpochRef.current;
    apps
      .list(currentProject.id)
      .then((rows) => {
        if (epoch !== fetchEpochRef.current) return; // stale response
        const out: {
          name: string;
          to: string;
          label: string;
          icon?: string;
          iconStyle?: "image" | "monochrome";
          suggested?: boolean;
        }[] = [];
        const seen = new Set<string>();
        for (const r of rows) {
          if (r.status !== "running" && !r.serving) continue;
          for (const p of r.ui_panels || []) {
            if (p.slot !== "project.page") continue;
            const to = `/apps/${r.name}/page`;
            // Dedupe by route — multiple installs of the same app
            // (one per project) share /apps/<name>/page, and the
            // SidebarLink uses key={item.to}. Defensive against
            // any future cross-project leak from the API.
            if (seen.has(to)) continue;
            seen.add(to);
            out.push({
              name: r.name,
              to,
              label: p.label || r.display_name || r.name,
              icon: r.icon,
              iconStyle: r.icon_style,
              suggested: p.suggested,
            });
          }
        }
        setAppNav(out);
      })
      .catch(() => {
        if (epoch !== fetchEpochRef.current) return;
        setAppNav([]);
      });
  }, [currentProject?.id]);
  useEffect(() => {
    refreshAppNav();
    const onAppsChanged = () => refreshAppNav();
    window.addEventListener("apteva:apps-changed", onAppsChanged);
    const id = setInterval(refreshAppNav, 5000);
    return () => {
      window.removeEventListener("apteva:apps-changed", onAppsChanged);
      clearInterval(id);
    };
  }, [refreshAppNav]);

  const pinnedAppNames = new Set(
    preferredSidebarAppNames(appNav, projectUILayout),
  );
  const pinnedAppNav = appNav.filter((item) => pinnedAppNames.has(item.name));
  const sidebarQuery = new URLSearchParams(location.search);
  const selectedSidebarAgentID = sidebarQuery.get("agent");
  const creatingPersonalAgent = sidebarQuery.get("new") === "1";

  // Flat list — only used for "is this entry's path a prefix of
  // another's" active-link disambiguation. Doesn't change rendering.
  const navItems = [...primaryNav, ...pageNav, ...pinnedAppNav, ...manageNav];

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  const renderSidebar = (mobile = false, compact = !mobile && sidebarCollapsed) => (
    <>
      <div className={`h-[61px] border-b border-border flex items-center ${compact ? "justify-center px-2" : "justify-between px-5"}`}>
        {!compact && (
          <Link
            to="/"
            onClick={() => {
              if (mobile) setMobileNavOpen(false);
            }}
            className="text-accent font-bold text-lg"
          >
            Apteva
          </Link>
        )}
        {mobile && (
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="h-8 w-8 inline-flex items-center justify-center rounded border border-border text-text-muted hover:text-text hover:bg-bg-hover"
            aria-label="Close navigation"
          >
            x
          </button>
        )}
        {!mobile && (
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            className="h-9 w-9 inline-flex shrink-0 items-center justify-center rounded text-text-muted hover:text-text hover:bg-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            aria-label={compact ? "Expand sidebar" : "Collapse sidebar"}
            title={compact ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" />
              <path d={compact ? "m14 9 3 3-3 3" : "m17 9-3 3 3 3"} />
            </svg>
          </button>
        )}
      </div>

      {/* Project selector. Projects the current user didn't create
          (typically because they were invited as a member, or because
          they're a platform admin seeing every project) get a
          "(shared)" tag so the picker UI signals "you're peeking
          into someone else's workspace." */}
      {projects.length > 0 && (
        <div data-tour="workspace" className="px-3 py-3 border-b border-border">
          {compact ? (
            <button
              type="button"
              onClick={toggleSidebarCollapsed}
              className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-bg-input text-text-muted hover:text-text"
              aria-label={`Select project: ${currentProject?.name || "none"}`}
              title={currentProject?.name || "Select project"}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 6h6l2 2h10v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
            </button>
          ) : (
            <select
              value={currentProject?.id || ""}
              onChange={(e) => {
                const p = projects.find((p) => p.id === e.target.value);
                setCurrentProject(p || null);
              }}
              className="w-full bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:border-accent"
            >
              {projects.map((p) => {
                const mine = !!user && p.user_id === user.id;
                return (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {mine ? "" : " (shared)"}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      )}

      {/* Primary call-to-action — building a new agent is the
          highest-frequency creative action in the app, so it sits
          above the nav rail (not behind a "go to Agents → click +"
          two-step). Filled accent so it stays the focal point of
          the sidebar regardless of which page is selected. */}
      {/* One shared CTA slot. Personal points it at the focused creation
          state; Business and Developer point at the advanced wizard. */}
      <div className="px-3 py-3 border-b border-border">
        <NewAgentButton
          to={audience === "personal" ? "/?new=1" : "/agents/new"}
          label={t("nav.newAgent")}
          onClick={() => {
            if (mobile) setMobileNavOpen(false);
          }}
          className={compact ? "" : "w-full"}
          iconOnly={compact}
          title={t("nav.newAgentTitle")}
        />
      </div>

      <div className="flex-1 py-3 overflow-y-auto">
        {/* Primary group — the platform's daily-use verbs.
            No section label: this group IS the dashboard's default. */}
        {primaryNav.map((item) => (
          <SidebarLink
            key={item.to}
            item={item}
            navItems={navItems}
            compact={compact}
            onNavigate={mobile ? () => setMobileNavOpen(false) : undefined}
          />
        ))}

        {pageNav.length > 0 && <>
          <SidebarSectionHeader label={t("nav.pages")} compact={compact} />
          {pageNav.map((item) => <SidebarLink key={item.to} item={item} navItems={navItems} compact={compact} onNavigate={mobile ? () => setMobileNavOpen(false) : undefined} />)}
        </>}

        {audience === "personal" && (
          <div data-tour="personal-agents">
            <SidebarSectionHeader label={t("nav.agents")} compact={compact} />
            {sidebarAgents.map((agent) => (
              <SidebarAgentLink
                key={agent.id}
                agent={agent}
                compact={compact}
                active={
                  location.pathname === "/" &&
                  selectedSidebarAgentID === String(agent.id) &&
                  !creatingPersonalAgent
                }
                onNavigate={mobile ? () => setMobileNavOpen(false) : undefined}
              />
            ))}
            {sidebarAgents.length === 0 && !compact && (
              <div className="px-5 py-2 text-xs text-text-dim">No agents yet</div>
            )}
          </div>
        )}

        {/* Apps group — only rendered when >=1 installed app has a
            project.page panel. Header label tells the user these are
            contributed by sidecars, and each entry carries the app's
            own icon to reinforce the "third-party" visual cue.
            Caps the visible list at SIDEBAR_APPS_VISIBLE so an
            installer with 20+ apps doesn't bury the MANAGE section
            below the fold. Anything beyond the cap collapses behind
            a "More apps (N)" toggle. Preferred apps are stored in the
            current project's generic UI layout. */}
        {shows("nav.appPages") && appNav.length > 0 &&
          (() => {
            const visibleApps = compact || showAllApps
              ? pinnedAppNav
              : pinnedAppNav.slice(0, SIDEBAR_APPS_VISIBLE);
            const overflow = pinnedAppNav.length - visibleApps.length;
            return (
              <>
                {compact ? (
                  <SidebarSectionHeader label={t("nav.appsSection")} compact />
                ) : (
                <div className="group mt-5 mb-1 flex items-center pl-5 pr-3">
                  <button
                    type="button"
                    onClick={() => setSidebarAppsOpen(true)}
                    className="min-w-0 flex-1 text-left text-[10px] font-medium uppercase tracking-wider text-text-dim/70 hover:text-text"
                    aria-label="Manage sidebar apps"
                  >
                    {t("nav.appsSection")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSidebarAppsOpen(true)}
                    className="flex h-7 w-7 items-center justify-center rounded text-lg leading-none text-text-muted opacity-0 hover:bg-bg-hover hover:text-accent focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent group-hover:opacity-100"
                    aria-label="Pin or unpin sidebar apps"
                    title="Pin or unpin sidebar apps"
                  >
                    +
                  </button>
                </div>
                )}
                {visibleApps.map((item) => (
                  <SidebarLink
                    key={item.to}
                    item={item}
                    navItems={navItems}
                    iconUrl={item.icon ?? ""}
                    iconStyle={item.iconStyle}
                    compact={compact}
                    onNavigate={
                      mobile ? () => setMobileNavOpen(false) : undefined
                    }
                  />
                ))}
                {visibleApps.length === 0 && (
                  <button
                    type="button"
                    onClick={() => setSidebarAppsOpen(true)}
                    className={compact ? "mx-2 flex h-10 w-12 items-center justify-center rounded-lg text-lg text-text-muted hover:bg-bg-hover hover:text-text" : "w-full px-5 py-2 text-left text-xs text-text-dim hover:text-text"}
                    aria-label={compact ? "Choose preferred apps" : undefined}
                    title={compact ? "Choose preferred apps" : undefined}
                  >
                    {compact ? "+" : "Choose preferred apps…"}
                  </button>
                )}
                {!compact && (overflow > 0 || showAllApps) && (
                  <button
                    onClick={toggleShowAllApps}
                    className="w-full px-5 py-2 text-xs text-text-muted hover:text-text text-left transition-colors"
                  >
                    {showAllApps
                      ? t("nav.showLess")
                      : t("nav.moreApps", { count: overflow })}
                  </button>
                )}
              </>
            );
          })()}

        {/* Manage group — platform-administration verbs. Things you
            do TO the platform, not WITH the platform's daily surfaces. */}
        <SidebarSectionHeader label={t("nav.manageSection")} compact={compact} />
        {manageNav.map((item) => (
          <SidebarLink
            key={item.to}
            item={item}
            navItems={navItems}
            compact={compact}
            onNavigate={mobile ? () => setMobileNavOpen(false) : undefined}
          />
        ))}
      </div>

      {/* Logged-in user + account menu (change password, logout). Rendered
          above the version line so it sits in the same footer area. */}
      {user && (compact ? (
        <button
          type="button"
          onClick={toggleSidebarCollapsed}
          className="h-14 border-t border-border flex items-center justify-center text-text-muted hover:text-text hover:bg-bg-hover"
          aria-label={`Open account menu for ${user.email}`}
          title={user.email}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3" />
            <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
          </svg>
        </button>
      ) : <AccountMenu user={user} onLogout={logout} />)}

      {version && !compact && (
        <div className="px-5 py-3 border-t border-border flex items-center gap-2">
          <span className="text-text-muted text-xs" title={versionTip}>
            v{version}
          </span>
          {platformStatus && (
            <button
              type="button"
              onClick={() => setUpdateModalOpen(true)}
              className={`text-xs px-2 py-0.5 rounded transition-colors ${platformStatus.update_available ? "bg-yellow/15 text-yellow hover:bg-yellow/25" : "text-text-muted hover:bg-bg-hover"}`}
              title="Click for update details"
            >
              {platformStatus.update_available ? "update available" : "Updates"}
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <HostUIProvider scope={`${location.key}:${currentProject?.id || ""}`}>
    <div className="app-shell flex h-dvh min-h-dvh bg-bg overflow-hidden">
      {/* Sidebar */}
      <nav className={`hidden md:flex shrink-0 border-r border-border flex-col transition-[width] duration-200 ${sidebarCollapsed ? "w-16" : "w-56"}`} aria-label="Main navigation">
        {renderSidebar(false)}
      </nav>

      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
          />
          <nav className="relative h-full w-[min(20rem,86vw)] bg-bg border-r border-border flex flex-col shadow-xl safe-area-y safe-area-left">
            {renderSidebar(true)}
          </nav>
        </div>
      )}

      {updateModalOpen && platformStatus && (
        <PlatformUpdateModal
          status={platformStatus}
          refreshing={refreshing}
          onRefresh={refreshPlatformStatus}
          onClose={() => setUpdateModalOpen(false)}
        />
      )}

      {sidebarAppsOpen && currentProject?.id && (
        <SidebarAppManager
          projectId={currentProject.id}
          apps={appNav.map(({ name, label, icon, iconStyle, suggested }) => ({
            name,
            label,
            icon,
            iconStyle,
            suggested,
          }))}
          onClose={() => setSidebarAppsOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col">
        {/* Slim top bar — global controls. Currently just the
            notifications tray; placeholder for future search,
            user-shortcut, or quick-create surfaces. */}
        <div
          className="app-topbar-safe flex border-b border-border items-center justify-between md:justify-end gap-3 flex-shrink-0"
        >
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-lg border border-border text-text-muted hover:text-text hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-accent"
            aria-label="Open navigation"
          >
            <span className="sr-only">Open navigation</span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 7h16" />
              <path d="M4 12h16" />
              <path d="M4 17h16" />
            </svg>
          </button>
          <div className="md:hidden min-w-0 flex-1 leading-tight">
            <div className="text-base font-bold text-accent truncate">Apteva</div>
            {currentProject && (
              <div className="text-xs text-text-dim truncate" title={currentProject.name}>
                {currentProject.name}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
          {PRODUCT_TOUR.enabled && !tourDismissed && <button type="button" data-tour="replay" onClick={requestProductTour} title="Explore Apteva" aria-label="Explore Apteva" className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold text-text-muted hover:border-accent hover:text-text">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m16 8-2 6-6 2 2-6z" /></svg><span className="hidden sm:inline">Explore Apteva</span>
          </button>}
          <NotificationsTray />
          </div>
        </div>
        <div data-tour="page" className="flex-1 min-h-0 overflow-hidden">
          <Suspense fallback={<p className="p-4 text-sm text-text-muted">Loading…</p>}><Outlet /></Suspense>
        </div>
      </main>

      <RealtimeVoiceDock />
      <ChatAssistantDock />
      <ProductTour key={user ? user.id : "guest"} navPaths={navItems.map((item) => item.to)} onStart={beginTour} onPrepare={prepareTour} onEnd={endTour} />
    </div>
    </HostUIProvider>
  );
}

// Installation instructions remain available alongside the persistent updater.
function PlatformUpdateModal(props: {
  status: PlatformStatus;
  refreshing: boolean;
  onRefresh: () => void;
  onClose: () => void;
}) {
  const { status, refreshing, onRefresh, onClose } = props;
  const polled = status.polled_at
    ? new Date(status.polled_at).toLocaleString()
    : "never";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg border border-border rounded-lg shadow-xl max-w-lg w-full max-h-[90dvh] overflow-y-auto p-4 sm:p-6"
        role="dialog" aria-modal="true" aria-label="Platform updates"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium">{status.update_available ? "Platform update available" : "Platform updates"}</h2>
            {status.bundle_version && (
              <p className="text-text-muted text-xs mt-0.5">
                Bundle v{status.bundle_version}
                {status.release_notes_url && (
                  <>
                    {" · "}
                    <a
                      href={status.release_notes_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline"
                    >
                      release notes ↗
                    </a>
                  </>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="space-y-1 mb-4">
          {status.components.map((c) => (
            <div
              key={c.name}
              className="flex items-center justify-between text-sm py-1 border-b border-border last:border-b-0"
            >
              <span className="font-mono text-xs">{c.name}</span>
              <span className="text-text-muted">
                v{c.current || "?"}
                {c.update_available && c.latest && (
                  <span className="text-yellow"> → v{c.latest}</span>
                )}
              </span>
            </div>
          ))}
        </div>

        <PlatformUpdateAction status={status} />

        <details className="rounded border border-border p-3 mb-4 text-xs" open={status.install_method === "source" || status.install_method === "docker" || status.install_method === "packaged"}>
          <summary className="cursor-pointer font-medium">Update from the terminal</summary>
          <div className="font-medium mb-2">
            {updateInstructionsTitle(status.install_method)}
          </div>
          <pre className="font-mono text-text-muted whitespace-pre-wrap leading-relaxed">
            {updateInstructionsBody(status.install_method)}
          </pre>
          {updateInstructionsNote(status.install_method) && (
            <div className="mt-2 text-text-dim text-[11px] italic">
              {updateInstructionsNote(status.install_method)}
            </div>
          )}
        </details>

        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>Last checked: {polled}</span>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="px-3 py-1 rounded border border-border hover:bg-border/30 transition-colors disabled:opacity-50"
          >
            {refreshing ? "Checking…" : "Check now"}
          </button>
        </div>

        {status.error && (
          <div className="mt-3 text-xs text-red">Error: {status.error}</div>
        )}
      </div>
    </div>
  );
}

// SidebarSectionHeader — the small uppercase label that delineates
// nav groups (APPS / MANAGE). Same typographic treatment Linear, VS
// Code, and Slack use for sidebar groupings: tiny, low-contrast,
// generous top margin so the group reads as a separate band.
function SidebarSectionHeader({ label, compact = false }: { label: string; compact?: boolean }) {
  if (compact) {
    return <div role="separator" aria-label={label} className="mx-4 my-3 border-t border-border" />;
  }
  return (
    <div className="px-5 mt-5 mb-1 text-[10px] font-medium tracking-wider text-text-dim/70 uppercase">
      {label}
    </div>
  );
}

// SidebarLink — a NavLink with optional inline app-icon prefix, plus
// the same end-match behaviour the original flat list used (avoids
// /apps lighting up as active when you're on /apps/storage/page).
function SidebarLink({
  item,
  navItems,
  iconUrl,
  iconStyle,
  compact = false,
  onNavigate,
}: {
  item: { to: string; label: string };
  navItems: { to: string; label: string }[];
  iconUrl?: string;
  iconStyle?: "image" | "monochrome";
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const isPrefixOfAnother = navItems.some(
    (other) =>
      other !== item &&
      other.to.startsWith(item.to + (item.to === "/" ? "" : "/")),
  );
  const platformIcon = item.to.startsWith("/pages/") ? "pages" : platformNavIcons[item.to];
  return (
    <NavLink
      to={item.to}
      data-tour={`nav-${item.to}`}
      end={item.to === "/" || isPrefixOfAnother}
      onClick={onNavigate}
      title={compact ? item.label : undefined}
      aria-label={compact ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-2 text-sm transition-colors ${compact ? "mx-2 h-10 justify-center rounded-lg px-0" : "px-5 py-2"} ${
          isActive
            ? compact ? "text-accent bg-bg-hover ring-1 ring-accent/30" : "text-accent bg-bg-hover border-r-2 border-accent"
            : "text-text-muted hover:text-text hover:bg-bg-hover"
        }`
      }
    >
      {iconUrl !== undefined ? (
        <AppIcon
          src={iconUrl}
          iconStyle={iconStyle}
          name={item.label}
          size={compact ? "sm" : "xs"}
          framed={false}
          className="text-accent"
        />
      ) : platformIcon ? (
        <SidebarPlatformIcon name={platformIcon} compact={compact} />
      ) : null}
      <span className={compact ? "sr-only" : "truncate"}>{item.label}</span>
    </NavLink>
  );
}

function SidebarPlatformIcon({ name, compact = false }: { name: PlatformNavIcon; compact?: boolean }) {
  const paths: Record<PlatformNavIcon, ReactNode> = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
    pages: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
    agents: <><circle cx="9" cy="8" r="3" /><path d="M3 20v-2a6 6 0 0 1 12 0v2" /><path d="M17 5a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 5v1" /></>,
    monitor: <><path d="M3 12h4l3-7 4 14 3-7h4" /></>,
    integrations: <><path d="M8 3v5m8-5v5M6 8h12v4a6 6 0 0 1-12 0zM12 18v3" /></>,
    apps: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M17.5 14v7m-3.5-3.5h7" /></>,
    skills: <><path d="M12 3 4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7z" /><path d="m9 12 2 2 4-4" /></>,
    usage: <><path d="M4 20V12m5 8V8m5 12V4m5 16v-6M2 20h20" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M10 3h4l.7 2.1 1.7.7 2-.9 2.8 2.8-.9 2 .7 1.7L23 12l-2.1.7-.7 1.7.9 2-2.8 2.8-2-.9-1.7.7L14 21h-4l-.7-2.1-1.7-.7-2 .9-2.8-2.8.9-2-.7-1.7L1 12l2.1-.7.7-1.7-.9-2 2.8-2.8 2 .9 1.7-.7z" /></>,
  };
  return (
    <svg
      className={`${compact ? "h-5 w-5" : "h-4 w-4"} shrink-0`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function SidebarAgentLink({
  agent,
  active,
  compact = false,
  onNavigate,
}: {
  agent: Agent;
  active: boolean;
  compact?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={`/?agent=${agent.id}`}
      onClick={onNavigate}
      title={compact ? agent.name : undefined}
      aria-label={compact ? agent.name : undefined}
      className={`mx-2 mb-1 flex items-center gap-2.5 rounded-lg transition-colors ${compact ? "h-10 justify-center px-0" : "px-3 py-2"} ${
        active
          ? "bg-bg-hover text-text"
          : "text-text-muted hover:bg-bg-hover hover:text-text"
      }`}
    >
      <AgentMark icon={agent.icon} color={agent.icon_color} size="sm" />
      <span className={compact ? "sr-only" : "min-w-0 flex-1"}>
        <span className="block truncate text-xs font-medium">{agent.name}</span>
        <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-text-dim">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              agent.status === "running" ? "bg-green" : "bg-text-dim"
            }`}
          />
          {agent.status === "running" ? "Ready" : agent.status}
        </span>
      </span>
    </Link>
  );
}

// updateInstructionsTitle / Body / Note — the three slots in the
// "Platform update available" modal. The server tags each install
// with an `install_method` (foreground / systemd-* / launchd-* /
// docker / source / packaged); we render the matching command.
//
// Why three functions and not one big switch component: the title
// changes the verb ("To update, run" vs "Restart your container"),
// and the note appears only for flavors where the supervisor
// handles the restart for free — separating them keeps the JSX
// above clean.
function updateInstructionsTitle(method: string | undefined): string {
  switch (method) {
    case "docker":
      return "Pull the new image and restart your container:";
    case "source":
      return "Pull the monorepo and rebuild:";
    case "packaged":
      return "Use your system package manager:";
    case "systemd-system":
    case "systemd-user":
    case "launchd-system":
    case "launchd-user":
      return "Run apteva update — the supervisor handles the restart:";
    default:
      return "To update, run:";
  }
}

function updateInstructionsBody(method: string | undefined): string {
  switch (method) {
    case "docker":
      return "docker pull apteva:latest\ndocker compose up -d";
    case "source":
      return "# Update the component repositories, then from the workspace:\n./scripts/build-local.sh";
    case "packaged":
      return "# pick the right one for your distro\nsudo apt upgrade apteva\n# or: sudo dnf upgrade apteva\n# or: sudo pacman -Syu apteva";
    case "systemd-user":
    case "launchd-user":
      return "apteva update";
    case "systemd-system":
    case "launchd-system":
      return "sudo apteva update";
    default:
      // foreground OR unknown: show the canonical paths for both
      // standalone-tarball and npm installs so npx/npm-global users
      // see something useful too.
      return "# standalone / one-command upgrade\napteva update\n\n# npm install\nnpm install -g apteva@latest";
  }
}

function updateInstructionsNote(method: string | undefined): string | null {
  switch (method) {
    case "systemd-user":
    case "systemd-system":
      return "Apteva restarts the service and checks that it is healthy after the update.";
    case "launchd-user":
    case "launchd-system":
      return "Apteva restarts the service and checks that it is healthy after the update.";
    case "foreground":
      return "After the swap, re-run apteva to start the new version.";
    default:
      return null;
  }
}
